import { useCallback, useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "utils/api";
import { ANALYTICS_EVENTS, trackEvent } from "utils/analytics";
import { parseApiError } from "utils/parseApiError";
import { HTTP_STATUS_CONFLICT } from "../../constants";
import type { FloorPlanRecord } from "components/SmartSurveyor/SmartSurveyorForRudolfFritz/hooks/types";
import type { FloorPlanFolderRecord } from "../../utils/types";
import type {
  CreateFolderVariables,
  MovePlanState,
  MovePlanVariables,
  UseMovePlanToFolderArgs,
  UseMovePlanToFolderResult,
} from "./types";

export const useMovePlanToFolder = ({
  planRecords,
  onPlanFolderChanged,
  onFoldersCreated,
}: UseMovePlanToFolderArgs): UseMovePlanToFolderResult => {
  const { t } = useTranslation();

  const [movePlanState, setMovePlanState] = useState<MovePlanState>({
    phase: "closed",
  });

  const createFolderMutation = useMutation({
    mutationFn: ({
      projectId,
      folderName,
    }: CreateFolderVariables): Promise<FloorPlanFolderRecord> =>
      api.createQuantitySurveyorFloorPlanFolder({
        projectId,
        name: folderName,
      }),
    onSuccess: (folder) => {
      onFoldersCreated?.([folder]);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_FOLDER_CREATED);
    },
    onError: (error) => {
      const isNameTaken =
        parseApiError({ error }).status === HTTP_STATUS_CONFLICT;
      toast.error(
        t(
          isNameTaken
            ? "smartSurveyor.folders.nameTaken"
            : "smartSurveyor.folders.createFailed",
        ),
      );
    },
  });

  const movePlanMutation = useMutation({
    mutationFn: ({
      floorPlanId,
      folderId,
    }: MovePlanVariables): Promise<FloorPlanRecord> =>
      api.setQuantitySurveyorFloorPlanFolder(floorPlanId, folderId),
    onSuccess: (movedPlan, { floorPlanId }) => {
      onPlanFolderChanged?.(floorPlanId, movedPlan.folderId);
      trackEvent(ANALYTICS_EVENTS.SMART_SURVEYOR_PLAN_MOVED_TO_FOLDER, {
        destination: movedPlan.folderId === null ? "root" : "folder",
      });
      setMovePlanState({ phase: "closed" });
    },
    onError: () => {
      toast.error(t("smartSurveyor.folders.moveFailed"));
    },
  });

  const { mutateAsync: createFolderRequest } = createFolderMutation;
  const { mutate: movePlan } = movePlanMutation;

  const movingPlanRecord = useMemo(
    () =>
      movePlanState.phase === "closed"
        ? null
        : (planRecords.find(
            (record) => record.id === movePlanState.floorPlanId,
          ) ?? null),
    [movePlanState, planRecords],
  );

  const openMovePlan = useCallback((floorPlanId: number) => {
    setMovePlanState({ phase: "open", floorPlanId });
  }, []);

  const closeMovePlan = useCallback(() => {
    setMovePlanState({ phase: "closed" });
  }, []);

  const createFolder = useCallback(
    async (folderName: string) => {
      if (movingPlanRecord === null) return null;
      try {
        const folder = await createFolderRequest({
          projectId: movingPlanRecord.projectId,
          folderName,
        });
        return folder.id;
      } catch {
        // Already reported by onError.
        return null;
      }
    },
    [movingPlanRecord, createFolderRequest],
  );

  const submitMovePlan = useCallback(
    (folderId: number | null) => {
      if (movingPlanRecord === null) return;
      movePlan({ floorPlanId: movingPlanRecord.id, folderId });
    },
    [movingPlanRecord, movePlan],
  );

  return {
    movingPlanRecord,
    isCreatingFolder: createFolderMutation.isPending,
    isMoving: movePlanMutation.isPending,
    openMovePlan,
    closeMovePlan,
    createFolder,
    submitMovePlan,
  };
};
